import { useMemo } from 'react';
import { selector, useRecoilState, useRecoilValue } from 'recoil';
import {
  currentUserWorkspaceUserMembershipsState,
  workspacesCollectionState,
  workspaceScoresState,
  workspacesDashboardsState,
  workspacesLoadedState,
  workspacesState,
  workspaceUserMembershipsState
} from '../../store/workspacesAtoms';
import { WorkspaceUserMembershipStatus } from '../../types/common';
import { Workspace, WorkspaceUserMembership } from '../../types/files';
import ArrayUtils from '../../utils/ArrayUtils';

const currentUserWorkspaceUserMembershipsSelector = selector({
  key: 'currentUserWorkspaceUserMembershipsSelector',
  get: ({ get }) => {
    const currentUserWorkspaceUserMemberships = get(currentUserWorkspaceUserMembershipsState);

    const acceptedMemberships = currentUserWorkspaceUserMemberships
      ?.filter(membership => membership.status === WorkspaceUserMembershipStatus.ACCEPTED)
      .map(membership => ({
        id: membership.workspaceId,
        name: membership.name,
        group: membership.workspaceGroup,
        createdAt: membership.createdAt,
        updatedAt: membership.updatedAt,
        isPublic: true,
        isShared: true,
      })) ?? [];
    const pendingMemberships = currentUserWorkspaceUserMemberships
      ?.filter(membership => membership.status === WorkspaceUserMembershipStatus.PENDING)
      .map(membership => ({
        id: membership.workspaceId,
        name: membership.name,
        group: membership.workspaceGroup,
        createdAt: membership.createdAt,
        updatedAt: membership.updatedAt,
        isPublic: true,
        isShared: true,
      })) ?? [];

    const acceptedWorkspaces = ArrayUtils.sortByDescending(acceptedMemberships, 'createdAt') as (Workspace & { group?: string })[];
    const pendingWorkspaces = ArrayUtils.sortByDescending(pendingMemberships, 'createdAt') as (Workspace & { group?: string })[];

    return { acceptedWorkspaces, pendingWorkspaces };
  },
});

const workspaceUserMembershipsSelector = selector({
  key: 'workspaceUserMembershipsSelector',
  get: ({ get }) => {
    const workspaceUserMemberships = get(workspaceUserMembershipsState);

    const acceptedMemberships = workspaceUserMemberships
      ?.filter(shared => shared.status === WorkspaceUserMembershipStatus.ACCEPTED) ?? [];
    const pendingMemberships = workspaceUserMemberships
      ?.filter(shared => shared.status === WorkspaceUserMembershipStatus.PENDING) ?? [];

    const acceptedInvestors = ArrayUtils.sortByDescending(acceptedMemberships, 'createdAt') as WorkspaceUserMembership[];
    const pendingInvestors = ArrayUtils.sortByDescending(pendingMemberships, 'createdAt') as WorkspaceUserMembership[];

    return { acceptedInvestors, pendingInvestors };
  },
});

const useWorkspacesContext = () => {
  const [workspacesLoaded, setWorkspacesLoaded] = useRecoilState(workspacesLoadedState);
  const [workspaces, setWorkspaces] = useRecoilState(workspacesState);
  const [workspacesDashboards, setWorkspacesDashboards] = useRecoilState(workspacesDashboardsState);
  const [workspacesCollection, setWorkspacesCollection] = useRecoilState(workspacesCollectionState);
  const [currentUserWorkspaceUserMemberships, setCurrentUserWorkspaceUserMemberships] = useRecoilState(currentUserWorkspaceUserMembershipsState);
  const [workspaceUserMemberships, setWorkspaceUserMemberships] = useRecoilState(workspaceUserMembershipsState);
  const [workspaceScores, setWorkspaceScores] = useRecoilState(workspaceScoresState);
  const currentUserWorkspaceUserMembershipsStatusGroup = useRecoilValue(currentUserWorkspaceUserMembershipsSelector);
  const workspaceUserMembershipsStatusGroup = useRecoilValue(workspaceUserMembershipsSelector);

  const context = useMemo(() => ({
    workspacesLoaded,
    workspaces,
    workspacesDashboards,
    workspacesCollection,
    currentUserWorkspaceUserMemberships,
    workspaceUserMemberships,
    workspaceScores,
    currentUserWorkspaceUserMembershipsStatusGroup,
    workspaceUserMembershipsStatusGroup,
    setWorkspacesLoaded,
    setWorkspaces,
    setWorkspacesDashboards,
    setWorkspacesCollection,
    setCurrentUserWorkspaceUserMemberships,
    setWorkspaceUserMemberships,
    setWorkspaceScores
  }), [
    workspacesLoaded,
    workspaces,
    workspacesDashboards,
    workspacesCollection,
    currentUserWorkspaceUserMemberships,
    workspaceUserMemberships,
    workspaceScores,
    currentUserWorkspaceUserMembershipsStatusGroup,
    workspaceUserMembershipsStatusGroup,
    setWorkspacesLoaded,
    setWorkspaces,
    setWorkspacesDashboards,
    setWorkspacesCollection,
    setCurrentUserWorkspaceUserMemberships,
    setWorkspaceUserMemberships,
    setWorkspaceScores
  ]);

  return context;
};

export default useWorkspacesContext;